import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { lotteryApi } from '../../api/lottery.js';
import { ApiError } from '../../api/client.js';
import type { Product, ProductInput } from '../../api/types.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, Field, SortableTh, SearchInput } from '../../components/ui.js';
import { useUrlUpdate, useUrlSearch } from '../../lib/hooks.js';
import { text, money, num, dateOnly } from '../../lib/format.js';
import {
  checkImageUrl,
  checkLen,
  checkRequired,
  checkDateRange,
  hasFieldErrors,
  inputCls,
  type FieldErrors,
} from '../../lib/formValidation.js';
import { LotterySettingsModal } from './LotterySettingsModal.js';

/** 원본: lottery/product/product_list.php */
export function ProductsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1');
  const sort = params.get('sort') ?? 'lotteryId';
  const dir = (params.get('dir') ?? 'desc') as 'asc' | 'desc';
  const categoryCode = params.get('categoryCode') ?? '';

  const update = useUrlUpdate(params, setParams);
  const [search, setSearch] = useUrlSearch(params, update);
  const debounced = params.get('q') ?? '';

  const [formModal, setFormModal] = useState<{ mode: 'edit' | 'copy'; product: Product } | null>(null);
  // 원본 product_list.php 오른쪽 위 '설정' (settings.php - 교환기능·폭탄구매·배너 이미지).
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  useEffect(() => {
    if (!settingsSaved) return;
    const timer = window.setTimeout(() => setSettingsSaved(false), 4000);
    return () => window.clearTimeout(timer);
  }, [settingsSaved]);

  const categories = useQuery({
    queryKey: ['lottery', 'categories', 'all'],
    queryFn: () => lotteryApi.categories({ page: 1, perPage: 200 }),
    staleTime: 10 * 60 * 1000,
  });

  const query = useQuery({
    queryKey: ['lottery', 'products', { page, sort, dir, q: debounced, categoryCode }],
    queryFn: () =>
      lotteryApi.products({
        page,
        perPage: 30,
        sort,
        dir,
        q: debounced || undefined,
        categoryCode: categoryCode || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['lottery', 'products'] });
  }

  return (
    <section>
      <header className="page-head">
        <h1 className="page-title">랜덤박스 관리</h1>
        <div className="page-head__actions">
          {query.isFetching && <span className="page-head__busy">갱신 중…</span>}
          {settingsSaved && (
            <span className="td-ok" role="status">
              설정이 수정되었습니다.
            </span>
          )}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              setSettingsSaved(false);
              setSettingsOpen(true);
            }}
          >
            설정
          </button>
        </div>
      </header>

      <div className="filters">
        <Field label="검색 (상품명 · 일본어명)">
          <SearchInput value={search} placeholder="상품명" onChange={setSearch} />
        </Field>
        <Field label="카테고리">
          <select className="input" value={categoryCode} onChange={(e) => update({ categoryCode: e.target.value })}>
            <option value="">전체</option>
            {categories.data?.items.map((c) => (
              <option key={c.companyCode ?? c.seq} value={c.companyCode ?? ''}>
                {text(c.companyName)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {query.isPending ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorBox error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyBox />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableTh label="ID" sortKey="lotteryId" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <th>상품</th>
                  <th>카테고리</th>
                  <SortableTh label="가격" sortKey="price" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <SortableTh label="판매 시작" sortKey="saleStart" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <SortableTh label="판매 종료" sortKey="saleEnd" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <SortableTh label="노출 순위" sortKey="priority" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((p, i) => (
                  <tr
                    key={p.lotteryId ?? i}
                    className={p.lotteryId ? 'table__row--clickable' : undefined}
                    onClick={
                      p.lotteryId ? () => navigate(`/lottery/products/${p.lotteryId}/prizes`) : undefined
                    }
                  >
                    <td className="td-right td-muted">{num(p.lotteryId)}</td>
                    <td>
                      <span className="product-cell">
                        {p.thumbnail && <img className="thumb" src={p.thumbnail} alt="" loading="lazy" />}
                        <span>
                          {text(p.jpName ?? p.name)}
                          {p.name && p.jpName && <span className="td-sub">{p.name}</span>}
                        </span>
                      </span>
                    </td>
                    <td>{text(p.categoryName)}</td>
                    <td className="td-right">{money(p.price)}</td>
                    <td className="td-muted">{dateOnly(p.saleStart)}</td>
                    <td className="td-muted">{dateOnly(p.saleEnd)}</td>
                    <td className="td-right">{num(p.priorityRank)}</td>
                    {/* 행 클릭은 경품 관리로 이동하므로, 관리 칸에서는 이동을 막는다. */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="toolbar toolbar--tight">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setFormModal({ mode: 'edit', product: p })}
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setFormModal({ mode: 'copy', product: p })}
                        >
                          복사
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            total={query.data.total}
            onChange={(p) => update({ page: p }, false)}
          />
        </>
      )}

      {settingsOpen && (
        <LotterySettingsModal
          onClose={() => setSettingsOpen(false)}
          onSaved={() => {
            setSettingsOpen(false);
            setSettingsSaved(true);
          }}
        />
      )}

      {formModal && (
        <ProductFormModal
          mode={formModal.mode}
          product={formModal.product}
          categories={categories.data?.items ?? []}
          onClose={() => setFormModal(null)}
          onRefresh={refresh}
        />
      )}
    </section>
  );
}

/**
 * 폼이 들고 있는 값. server lottery/index.ts 의 createProductCrud 필드 목록과
 * 1:1 로 맞춰 둔다 - PATCH 가 필드 전체를 UPDATE 하므로, 여기서 빠진 칸은
 * 저장할 때마다 빈 값으로 덮인다 (A~E 경품 상품명이 그래서 폼에 들어 있다).
 */
interface ProductFormState {
  categoryCode: string;
  name: string;
  jpName: string;
  saleStart: string;
  saleStartTime: string;
  saleEnd: string;
  deliveryDate: string;
  thumbnail: string;
  movie: string;
  image1: string;
  image2: string;
  price: string;
  discountRate: string;
  priorityRank: string;
  bakugai: string;
  bakugaiNum: string;
  orderColor: string;
  memo: string;
  aPrizeProduct: string;
  bPrizeProduct: string;
  cPrizeProduct: string;
  dPrizeProduct: string;
  ePrizeProduct: string;
}

const numToStr = (v: number | null | undefined, fallback = ''): string =>
  v !== null && v !== undefined ? String(v) : fallback;

/**
 * 판매 시작 시각. 원본 product_modify.php 의 sale_start_time(00:00/10:00/18:00)을
 * 그대로 옮겼다. 값이 없으면 원본 기본값 18:00 (원본 select 는 값이 없을 때
 * 10:00·18:00 둘 다 selected 라 마지막 18:00 이 골라졌다). 실데이터 69건은 전부
 * 18:00 시작이다 (2026-11-05). 목록에 없는 시각이 저장돼 있으면 그 값도 선택지에
 * 넣어, 모달을 열고 저장만 해도 시각이 바뀌지 않게 한다.
 */
const SALE_START_TIMES = ['00:00', '10:00', '18:00'];

function saleStartTimeOf(dateTime: string | null): string {
  if (dateOnly(dateTime) === '-') return '18:00';
  const m = /^\d{4}-\d{2}-\d{2}[ T](\d{2}:\d{2})/.exec(dateTime ?? '');
  return m ? m[1]! : '18:00';
}

function toProductFormState(p: Product): ProductFormState {
  return {
    categoryCode: p.categoryCode ?? '',
    name: p.name ?? '',
    jpName: p.jpName ?? '',
    saleStart: dateOnly(p.saleStart) === '-' ? '' : dateOnly(p.saleStart),
    saleStartTime: saleStartTimeOf(p.saleStart),
    saleEnd: dateOnly(p.saleEnd) === '-' ? '' : dateOnly(p.saleEnd),
    deliveryDate: p.deliveryDate ?? '',
    thumbnail: p.thumbnail ?? '',
    movie: p.movie ?? '',
    image1: p.image1 ?? '',
    image2: p.image2 ?? '',
    price: numToStr(p.price),
    discountRate: numToStr(p.discountRate),
    priorityRank: numToStr(p.priorityRank, '0'),
    bakugai: numToStr(p.bakugai, '0'),
    bakugaiNum: numToStr(p.bakugaiNum),
    orderColor: p.orderColor || '#ffffff',
    memo: p.memo ?? '',
    aPrizeProduct: p.aPrizeProduct ?? '',
    bPrizeProduct: p.bPrizeProduct ?? '',
    cPrizeProduct: p.cPrizeProduct ?? '',
    dPrizeProduct: p.dPrizeProduct ?? '',
    ePrizeProduct: p.ePrizeProduct ?? '',
  };
}

const numOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v));

function toProductInput(form: ProductFormState): ProductInput {
  return {
    categoryCode: form.categoryCode,
    name: form.name,
    jpName: form.jpName,
    // 시작은 날짜 + 고른 시각, 종료는 원본처럼 그날 23:59:59 - 날짜만 저장하면
    // 00:00:00 이 되어 판매 마감이 하루 앞당겨진다.
    saleStart: `${form.saleStart} ${form.saleStartTime}:00`,
    saleEnd: `${form.saleEnd} 23:59:59`,
    deliveryDate: form.deliveryDate,
    thumbnail: form.thumbnail,
    movie: form.movie,
    image1: form.image1,
    image2: form.image2,
    price: Number(form.price) || 0,
    discountRate: numOrNull(form.discountRate),
    priorityRank: form.priorityRank === '' ? 0 : Number(form.priorityRank),
    bakugai: numOrNull(form.bakugai),
    bakugaiNum: numOrNull(form.bakugaiNum),
    orderColor: form.orderColor,
    memo: form.memo,
    aPrizeProduct: form.aPrizeProduct,
    bPrizeProduct: form.bPrizeProduct,
    cPrizeProduct: form.cPrizeProduct,
    dPrizeProduct: form.dPrizeProduct,
    ePrizeProduct: form.ePrizeProduct,
  };
}

/** 상품 수정/복사 모달. 원본 product_modify.php · product_copy.php 를 합쳤다. */
export function ProductFormModal({
  mode,
  product,
  categories,
  onClose,
  onRefresh,
}: {
  mode: 'edit' | 'copy';
  product: Product;
  categories: { companyCode: string | null; companyName: string | null }[];
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [form, setForm] = useState<ProductFormState>(() => toProductFormState(product));
  const lotteryId = Number(product.lotteryId);

  const [submitted, setSubmitted] = useState(false);
  /** 규칙은 서버 lottery/index.ts 의 필드 정의(maxLength)와 같은 값이다. */
  const errors: FieldErrors = {
    name: checkRequired(form.name, '상품명', 255),
    jpName: checkLen(form.jpName, 255, '일본어 상품명'),
    thumbnail: checkImageUrl(form.thumbnail),
    movie: checkImageUrl(form.movie),
    image1: checkImageUrl(form.image1),
    image2: checkImageUrl(form.image2),
    deliveryDate: checkLen(form.deliveryDate, 64, '발송 예정'),
    orderColor: checkLen(form.orderColor, 20, '색상'),
    memo: checkLen(form.memo, 5000, '관리용 메모'),
    saleEnd: checkDateRange(form.saleStart, form.saleEnd),
    aPrizeProduct: checkLen(form.aPrizeProduct, 255, 'A 경품 상품명'),
    bPrizeProduct: checkLen(form.bPrizeProduct, 255, 'B 경품 상품명'),
    cPrizeProduct: checkLen(form.cPrizeProduct, 255, 'C 경품 상품명'),
    dPrizeProduct: checkLen(form.dPrizeProduct, 255, 'D 경품 상품명'),
    ePrizeProduct: checkLen(form.ePrizeProduct, 255, 'E 경품 상품명'),
  };
  const shown: FieldErrors = submitted ? errors : {};

  // 복사는 상품을 먼저 만들고 경품을 한 줄씩 복제한 뒤 총확률을 다시 계산한다
  // (서버 product-crud.ts - 한 트랜잭션이 아니다). 중간에 실패해도 새 상품은
  // 남으므로, 모달을 닫지 않고 무엇이 빠졌는지 알린다 (fanza 복사와 같다).
  const [copyWarning, setCopyWarning] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = toProductInput(form);
      if (mode === 'edit') {
        await lotteryApi.updateProduct(lotteryId, body);
        return null;
      }
      return lotteryApi.copyProduct(lotteryId, body);
    },
    onSuccess: (result) => {
      onRefresh();
      if (result?.childCopyFailed) {
        setCopyWarning(
          `새 랜덤박스(#${result.id})는 만들었지만 경품 일부를 복사하지 못했습니다 (복사된 경품 ${result.copiedChildren}개). 새 랜덤박스의 경품 관리에서 빠진 경품을 확인해주세요.`,
        );
        return;
      }
      if (result?.probabilityRecalcFailed) {
        setCopyWarning(
          `새 랜덤박스(#${result.id})와 경품 ${result.copiedChildren}개는 복사했지만 총확률을 다시 계산하지 못했습니다. 새 랜덤박스의 경품 하나를 열어 저장하면 다시 계산됩니다.`,
        );
        return;
      }
      onClose();
    },
  });

  const valid = form.categoryCode && form.name && form.saleStart && form.saleEnd && form.price !== '';
  const bakugaiOn = form.bakugai === '1';

  const setField = (key: keyof ProductFormState) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">{mode === 'edit' ? '랜덤박스 수정' : '랜덤박스 복사'}</h2>

        {mode === 'copy' && (
          <p className="hint">
            <b>{text(product.jpName ?? product.name)}</b>(#{product.lotteryId})의 내용을 그대로 새
            랜덤박스로 만들고, <b>경품(등급·확률·수량)도 함께 복사합니다</b>. 총확률은 복사된
            경품으로 다시 계산됩니다. 필요하면 아래 값을 고친 뒤 저장하세요.
          </p>
        )}

        {/*
          2026-11-03: .form-grid(auto-fit 그리드)에 폭 전체 .row-2 가 섞여 있어
          브라우저가 row-2 들을 전부 같은 칸에 겹쳐 그렸다 - 폭구매 칸이 테마 색상
          칸을 덮어 색상 선택이 아예 안 보였다(styles.css 의 .form-stack 주석과 같은
          버그). DOM 순서대로만 쌓는 .form-stack 으로 바꾸고, 두 칸짜리 행은
          .row-2 로 직접 묶는다.
        */}
        <form
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (hasFieldErrors(errors)) return;
            save.mutate();
          }}
        >
          <Field label="카테고리">
            <select
              className="input"
              value={form.categoryCode}
              onChange={(e) => setField('categoryCode')(e.target.value)}
            >
              <option value="">카테고리 선택</option>
              {categories.map((c) => (
                <option key={c.companyCode} value={c.companyCode ?? ''}>
                  {c.companyName} ({c.companyCode})
                </option>
              ))}
            </select>
          </Field>

          <div className="row-2">
            <Field label="상품명" error={shown.name}>
              <input
                className={inputCls(shown.name)}
                value={form.name}
                maxLength={255}
                onChange={(e) => setField('name')(e.target.value)}
              />
            </Field>

            <Field label="일본어 상품명" error={shown.jpName}>
              <input
                className={inputCls(shown.jpName)}
                value={form.jpName}
                maxLength={255}
                onChange={(e) => setField('jpName')(e.target.value)}
              />
            </Field>
          </div>

          <div className="row-2 row-2--stack-sm">
            <Field label="판매 시작">
              <div className="date-hour-row">
                <input
                  className="input"
                  type="date"
                  value={form.saleStart}
                  onChange={(e) => setField('saleStart')(e.target.value)}
                />
                <select
                  className="input"
                  aria-label="판매 시작 시각"
                  value={form.saleStartTime}
                  onChange={(e) => setField('saleStartTime')(e.target.value)}
                >
                  {(SALE_START_TIMES.includes(form.saleStartTime)
                    ? SALE_START_TIMES
                    : [...SALE_START_TIMES, form.saleStartTime].sort()
                  ).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="판매 종료 (그날 23:59 까지)" error={shown.saleEnd}>
              <input
                className={inputCls(shown.saleEnd)}
                type="date"
                value={form.saleEnd}
                onChange={(e) => setField('saleEnd')(e.target.value)}
              />
            </Field>
          </div>

          <Field label="발송 예정 (자유 입력 · 예: 1월 하순 ~ 2월 하순)" error={shown.deliveryDate}>
            <input
              className={inputCls(shown.deliveryDate)}
              value={form.deliveryDate}
              maxLength={64}
              onChange={(e) => setField('deliveryDate')(e.target.value)}
            />
          </Field>

          <div className="row-2">
            <Field label="대표 이미지" error={shown.thumbnail}>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.thumbnail)}
                  value={form.thumbnail}
                  maxLength={255}
                  placeholder="https://..."
                  onChange={(e) => setField('thumbnail')(e.target.value)}
                />
                {form.thumbnail && <img className="thumb thumb--lg" src={form.thumbnail} alt="" />}
              </div>
            </Field>

            <Field label="동영상 URL" error={shown.movie}>
              <input
                className={inputCls(shown.movie)}
                value={form.movie}
                maxLength={255}
                placeholder="https://..."
                onChange={(e) => setField('movie')(e.target.value)}
              />
            </Field>
          </div>

          <div className="row-2">
            <Field label="소개 이미지 1" error={shown.image1}>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.image1)}
                  value={form.image1}
                  maxLength={255}
                  onChange={(e) => setField('image1')(e.target.value)}
                />
                {form.image1 && <img className="thumb thumb--lg" src={form.image1} alt="" />}
              </div>
            </Field>
            <Field label="소개 이미지 2" error={shown.image2}>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.image2)}
                  value={form.image2}
                  maxLength={255}
                  onChange={(e) => setField('image2')(e.target.value)}
                />
                {form.image2 && <img className="thumb thumb--lg" src={form.image2} alt="" />}
              </div>
            </Field>
          </div>

          <div className="row-3 row-3--even">
            <Field label="판매 가격">
              <input
                className="input"
                type="number"
                min={0}
                value={form.price}
                onChange={(e) => setField('price')(e.target.value)}
              />
            </Field>
            <Field label="표시 순위">
              <input
                className="input"
                type="number"
                value={form.priorityRank}
                onChange={(e) => setField('priorityRank')(e.target.value)}
              />
            </Field>
            {/* 원본 product_modify.php 의 '색상' - 주문관리 목록에서 이 랜덤박스 제목 색이 된다. */}
            <Field label="색상 (주문관리 제목 색)" error={shown.orderColor}>
              <div className="color-input-row">
                <input
                  className="input color-input"
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(form.orderColor) ? form.orderColor : '#ffffff'}
                  onChange={(e) => setField('orderColor')(e.target.value)}
                />
                <input className={inputCls(shown.orderColor)} value={form.orderColor} maxLength={20} readOnly />
              </div>
            </Field>
          </div>

          {/*
            폭탄구매(爆買い) - 원본 product_modify.php 처럼 사용/사용안함 라디오에
            [매수]·[할인율] 을 붙이고, 사용안함이면 둘 다 disabled 로 잠근다
            (2026-11-03 요청). 예전엔 "실제 값 의미를 모르니 숫자 칸 그대로 둔다"
            고 했는데, 실데이터를 보니 61건 전부 1 이고 컬럼도 tinyint(1)
            (기본값 1) 이라 0/1 로 좁혀도 잃는 값이 없다.

            원본은 disabled 칸이 폼 전송에서 빠져 사용안함으로 저장하면 매수·할인율이
            빈 값으로 덮였다. 여기서는 잠그기만 하고 값은 그대로 보낸다 - 다시
            사용으로 돌렸을 때 예전 값이 살아 있다.
          */}
          <div className="row-3 row-3--even">
            <Field label="폭탄구매" as="div">
              <div className="radio-group radio-group--compact" role="radiogroup" aria-label="폭탄구매">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="bakugai"
                    checked={bakugaiOn}
                    onChange={() => setField('bakugai')('1')}
                  />
                  사용
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="bakugai"
                    checked={!bakugaiOn}
                    onChange={() => setField('bakugai')('0')}
                  />
                  사용안함
                </label>
              </div>
            </Field>
            <Field label="매수">
              <input
                className="input"
                type="number"
                min={0}
                value={form.bakugaiNum}
                disabled={!bakugaiOn}
                onChange={(e) => setField('bakugaiNum')(e.target.value)}
              />
            </Field>
            <Field label="할인율 (%)">
              <input
                className="input"
                type="number"
                min={0}
                max={100}
                value={form.discountRate}
                disabled={!bakugaiOn}
                onChange={(e) => setField('discountRate')(e.target.value)}
              />
            </Field>
          </div>

          {([['a', 'b'], ['c', 'd'], ['e']] as const).map((pair) => (
            <div className="row-2" key={pair[0]}>
              {pair.map((g) => {
                const key = `${g}PrizeProduct` as keyof ProductFormState;
                return (
                  <Field key={g} label={`${g.toUpperCase()} 경품 상품명`} error={shown[key]}>
                    <input
                      className={inputCls(shown[key])}
                      value={form[key]}
                      maxLength={255}
                      onChange={(e) => setField(key)(e.target.value)}
                    />
                  </Field>
                );
              })}
            </div>
          ))}

          <Field label="관리용 메모" error={shown.memo}>
            <input
              className={inputCls(shown.memo)}
              value={form.memo}
              maxLength={5000}
              onChange={(e) => setField('memo')(e.target.value)}
            />
          </Field>

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          {copyWarning ? (
            <>
              <div className="qty-msg qty-msg--error" role="alert">
                {copyWarning}
              </div>
              <button type="button" className="btn btn--ghost btn--block" onClick={onClose}>
                닫기
              </button>
            </>
          ) : (
            <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
              {save.isPending ? '저장 중…' : mode === 'edit' ? '저장하기' : '복사본 만들기'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
